import { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Alert, Input } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { AlertTriangle, Eye, EyeOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { StepWrapper } from '@/common/components/ConnectionSteps/StepWrapper'
import {
  PasswordDefinitionForm,
  PasswordDefinitionSchema,
} from '../../schemas/ChangePasswordSchema'

type PasswordDefinitionStepProps = {
  appData: {
    applicationAccountUserId: string
    applicationName: string
    accountName: string
  }
  stepInfo: {
    current: number
    total: number
  }
  onValidationChange?: (isValid: boolean) => void
  onFormChange?: (values: unknown) => void
  isLastStep?: boolean
}

export function PasswordDefinitionStep({
  appData,
  stepInfo,
  onValidationChange,
  onFormChange,
  isLastStep = false,
}: PasswordDefinitionStepProps) {
  const { t } = useTranslation(['common', 'profile'])
  const [showNewPassword, setShowNewPassword] = useState(false)

  const getDisplayName = () => {
    if (appData.accountName && appData.accountName.trim()) {
      return `${appData.applicationName} (${appData.accountName})`
    }
    return appData.applicationName
  }

  const hookForm = useForm<PasswordDefinitionForm>({
    resolver: zodResolver(PasswordDefinitionSchema()),
    mode: 'onChange',
    defaultValues: {
      newPassword: '',
    },
  })

  const isFormValid = hookForm.formState.isValid
  const formValues = hookForm.watch()

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isFormValid)
    }
  }, [isFormValid, onValidationChange])

  useEffect(() => {
    if (onFormChange) {
      onFormChange(formValues)
    }
  }, [formValues, onFormChange])

  return (
    <FormProvider {...hookForm}>
      <StepWrapper
        appData={{
          applicationId: '',
          applicationName: appData.applicationName,
          userId: '',
        }}
        stepInfo={stepInfo}
        accountName={appData.accountName}
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600 mb-4">
            {t(
              'profile:access.modal.remove_access.password_definition.description',
              {
                displayName: getDisplayName(),
              },
            )}
          </div>

          <div className="w-[50%]">
            <Input.Icon
              size="lg"
              name="newPassword"
              label={t(
                'profile:access.modal.remove_access.password_definition.new_password_label',
                {
                  displayName: getDisplayName(),
                },
              )}
              type={showNewPassword ? 'text' : 'password'}
              placeholder={t(
                'profile:access.modal.remove_access.password_definition.new_password_placeholder',
              )}
              icon={
                showNewPassword ? (
                  <Eye
                    className="text-base-500 mt-2"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  />
                ) : (
                  <EyeOff
                    className="text-base-500 mt-2"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  />
                )
              }
              iconPosition="right"
            />
          </div>

          <Alert.Root>
            <Alert.Icon icon={<AlertTriangle />} />
            <Alert.Body>
              <Alert.Description>
                <div className="space-y-2">
                  <p>
                    {t(
                      'profile:access.modal.remove_access.password_definition.warning_message',
                      {
                        displayName: getDisplayName(),
                      },
                    )}
                  </p>
                  {isLastStep && (
                    <p>
                      {t(
                        'profile:access.modal.remove_access.type_selection.warning_message',
                      )}
                    </p>
                  )}
                </div>
              </Alert.Description>
            </Alert.Body>
          </Alert.Root>
        </div>
      </StepWrapper>
    </FormProvider>
  )
}
